import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  CircularProgress,
  Alert,
  Divider,
  Paper
} from '@mui/material';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import paymentService from '../../services/paymentService';
import stripeUtils from '../../utils/stripe';

// Stripe Elements configuration
const ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

// Test payment component
const TestPaymentForm = () => {
  const [amount, setAmount] = useState('10.00');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [clientSecret, setClientSecret] = useState('');
  const [paymentIntent, setPaymentIntent] = useState(null);
  
  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setSuccess(false);

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      setError('Stripe.js has not loaded yet. Please try again.');
      setLoading(false);
      return;
    }

    try {
      // 1. Create a payment intent on the server
      const amountInCents = Math.round(parseFloat(amount) * 100);
      const { clientSecret } = await paymentService.createPaymentIntent(
        amountInCents,
        'test-order-123',
        {
          test: true,
          description: 'Test payment from kiosk',
          metadata: {
            test: 'true',
            source: 'kiosk-test'
          }
        }
      );

      // 2. Confirm the card payment
      const { error: stripeError, paymentIntent: confirmedPaymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: 'Test User',
            },
          },
        }
      );

      if (stripeError) {
        throw new Error(stripeError.message || 'Payment failed');
      }

      setPaymentIntent(confirmedPaymentIntent);
      setSuccess(true);
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'An error occurred during payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 500, mx: 'auto', mt: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Test Payment Integration
        </Typography>
        <Divider sx={{ my: 2 }} />
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            Payment successful! Payment ID: {paymentIntent?.id}
          </Alert>
        ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              fullWidth
              margin="normal"
              inputProps={{ step: '0.01', min: '1.00' }}
              disabled={loading}
            />
            
            <Card variant="outlined" sx={{ p: 2, my: 2, backgroundColor: '#f9f9f9' }}>
              <Typography variant="subtitle2" gutterBottom>
                Card Details
              </Typography>
              <CardElement options={ELEMENT_OPTIONS} />
            </Card>
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              disabled={!stripe || loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Pay Now'}
            </Button>
          </form>
        )}
        
        {paymentIntent && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2">Payment Details:</Typography>
            <pre style={{ fontSize: '0.8rem', overflowX: 'auto' }}>
              {JSON.stringify(paymentIntent, null, 2)}
            </pre>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

// Wrapper component that loads Stripe
const PaymentTest = () => {
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    // Initialize Stripe with your publishable key
    const initializeStripe = async () => {
      const stripe = await stripeUtils.getStripe();
      setStripePromise(stripe);
    };
    
    initializeStripe();
  }, []);

  if (!stripePromise) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading payment processor...</Typography>
      </Box>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <TestPaymentForm />
    </Elements>
  );
};

export default PaymentTest;
